/* Servicestand van de zonnestroomkant.
   Port van greenvision-ems/web/src/data/service.js. Alles hieronder is afgeleid
   uit wat er al gelezen is: ems.asset, ems.meting en ems.ingest_run. Er wordt
   niets bijgeschat en niets geschreven.

   Afwijking van de bron: tijdTekst() krijgt de tijdzone van de locatie mee in
   plaats van een vaste zone, en bouwServicestatus() dus ook. */

import { eisZone } from './tijd';

const MIN = 60000;
const UUR = 60 * MIN;

/* Drempels. Bewust ruim: een signaal dat te vaak afgaat wordt genegeerd. */
export const DREMPEL = {
  achterMs: 2 * UUR,     // hoever een omvormer achter mag lopen op de snelste
  achterstandPct: 15,    // hoeveel procent een omvormer onder de beste mag zitten
  achterstandDagen: 3,   // over hoeveel dagen met data dat moet gelden
  gatPct: 60,            // kwartieren als aandeel van de mediaan van de rest
};

function mediaan(getallen: number[]): number {
  if (!getallen.length) return 0;
  const s = [...getallen].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function ouderdomTekst(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return 'nooit';
  if (ms < MIN) return 'zojuist';
  if (ms < UUR) return `${Math.round(ms / MIN)} min`;
  if (ms < 48 * UUR) return `${Math.round(ms / UUR)} uur`;
  return `${Math.round(ms / (24 * UUR))} dagen`;
}

/* De ingesttaken heten solis.backfill:<extern_id>. Zo hangt een run aan een
   omvormer zonder dat er een kolom voor nodig is. */
export function runBijAsset(runs: any[], externId: string | null): any {
  if (!externId) return null;
  const eigen = runs.filter((r) => r.taak && r.taak.endsWith(`:${externId}`));
  return eigen.length ? eigen[0] : null;
}

/**
 * @param assets    alle assets van de locatie
 * @param historie  dagenreeks uit bouwHistorie, oplopend
 * @param runs      rijen uit haalAssets, nieuwste eerst
 * @param nu        peilmoment
 * @param zone      ems.locatie.tijdzone — verplicht, voor de tijden in de signaaltekst
 */
export function bouwServicestatus(assets: any[], historie: any[], runs: any[], nu: Date, zone: string) {
  eisZone(zone);
  const omvormers = assets.filter((a) => a.type === 'omvormer');
  const nuMs = nu.getTime();
  const metData = historie.filter((d) => d.heeftData);
  const laatsteDag = metData.length ? metData[metData.length - 1] : null;

  /* laatste meting per omvormer, over het hele venster */
  const laatstePer = new Map<string, string>();
  for (const d of historie) {
    for (const o of d.omvormers) {
      if (!o.laatste) continue;
      const v = laatstePer.get(o.id);
      if (!v || o.laatste > v) laatstePer.set(o.id, o.laatste);
    }
  }
  const nieuwsteAlle = [...laatstePer.values()].sort().pop() || null;
  const nieuwsteMs = nieuwsteAlle ? new Date(nieuwsteAlle).getTime() : null;

  /* dagopbrengst per omvormer over de laatste dagen met data */
  const recent = metData.slice(-DREMPEL.achterstandDagen);
  const opbrengst = new Map<string, number[]>(omvormers.map((a) => [a.id, []]));
  for (const d of recent) {
    const meeDoen = d.omvormers.filter((o: any) => o.kwartieren > 0);
    const beste = Math.max(0, ...meeDoen.map((o: any) => o.kwh));
    /* Een omvormer met een half zo lange reeks heeft ook een half zo hoog
       dagtotaal. Dat is een gat en geen tegenvallende opbrengst. */
    const volledig = mediaan(meeDoen.map((o: any) => o.kwartieren)) * (DREMPEL.gatPct / 100);
    for (const o of d.omvormers) {
      if (beste > 0 && o.kwartieren > 0 && o.kwartieren >= volledig) {
        opbrengst.get(o.id)?.push((o.kwh / beste) * 100);
      }
    }
  }

  const regels = omvormers.map((a) => {
    const laatste = laatstePer.get(a.id) || null;
    const laatsteMs = laatste ? new Date(laatste).getTime() : null;
    const achterMs = laatsteMs !== null && nieuwsteMs !== null ? nieuwsteMs - laatsteMs : null;
    const deel = opbrengst.get(a.id) || [];
    const relatief = deel.length ? deel.reduce((x, y) => x + y, 0) / deel.length : null;
    const vandaag = laatsteDag ? laatsteDag.omvormers.find((o: any) => o.id === a.id) : null;
    return {
      id: a.id,
      naam: a.naam,
      model: [a.merk, a.model].filter(Boolean).join(' ') || 'onbekend',
      serienummer: a.serienummer || null,
      datalogger: a.datalogger_sn || null,
      externId: a.extern_id || null,
      laatste,
      ouderdomMs: laatsteMs === null ? null : nuMs - laatsteMs,
      achterMs,
      levert: laatste !== null && (achterMs === null || achterMs <= DREMPEL.achterMs),
      relatief,
      kwhLaatsteDag: vandaag ? vandaag.kwh : 0,
      kwartierenLaatsteDag: vandaag ? vandaag.kwartieren : 0,
      run: runBijAsset(runs, a.extern_id),
    };
  });

  /* ── signalen ────────────────────────────────────────────────
     Elk signaal wijst een asset aan en noemt de meting waar het uit volgt. */
  const signalen: any[] = [];

  for (const r of regels) {
    if (r.laatste === null) {
      signalen.push({
        id: `stil-${r.id}`, niveau: 'Verstoring', asset: r.naam,
        titel: `${r.naam} heeft geen enkele meting`,
        tekst: r.datalogger ? `Datalogger ${r.datalogger} leverde niets in dit venster.` : 'Geen meting in dit venster.',
        wanneer: null,
      });
    } else if (r.achterMs !== null && r.achterMs > DREMPEL.achterMs) {
      signalen.push({
        id: `achter-${r.id}`, niveau: 'Verstoring', asset: r.naam,
        titel: `${r.naam} loopt ${ouderdomTekst(r.achterMs)} achter`,
        tekst: `De laatste meting is van ${tijdTekst(r.laatste, zone)}. De andere omvormers zijn verder.`
          + (r.datalogger ? ` Datalogger ${r.datalogger}.` : ''),
        wanneer: r.laatste,
      });
    }
  }

  for (const r of regels) {
    if (r.relatief === null || r.relatief >= 100 - DREMPEL.achterstandPct) continue;
    const n = (opbrengst.get(r.id) || []).length;
    signalen.push({
      id: `laag-${r.id}`, niveau: 'Vraag', asset: r.naam,
      titel: `${r.naam} blijft ${Math.round(100 - r.relatief)}% achter bij de beste`,
      tekst: `Gemiddeld over ${n} ${n === 1 ? 'dag' : 'dagen'} met een volledige reeks. Kan schaduw, vervuiling of een string zijn.`,
      wanneer: r.laatste,
    });
  }

  if (laatsteDag) {
    const tellingen = laatsteDag.omvormers.filter((o: any) => o.kwartieren > 0).map((o: any) => o.kwartieren);
    const mid = mediaan(tellingen);
    for (const o of laatsteDag.omvormers) {
      if (!o.kwartieren || !mid) continue;
      if ((o.kwartieren / mid) * 100 >= DREMPEL.gatPct) continue;
      signalen.push({
        id: `gat-${o.id}`, niveau: 'Vraag', asset: o.naam,
        titel: `${o.naam} mist kwartieren op ${laatsteDag.sleutel}`,
        tekst: `${o.kwartieren} kwartieren tegenover ${Math.round(mid)} bij de rest. Backfill loopt mogelijk nog.`,
        wanneer: o.laatste,
      });
    }
  }

  for (const run of runs) {
    if (run.status === 'fout' || run.fout) {
      signalen.push({
        id: `run-${run.id}`, niveau: 'Kritiek', asset: 'Ingest',
        titel: `Ingesttaak ${run.taak} is gestrand`,
        tekst: run.fout || 'Zonder foutmelding afgebroken.',
        wanneer: run.gestart_op,
      });
    } else if (run.status === 'bezig' && nuMs - new Date(run.gestart_op).getTime() > UUR) {
      signalen.push({
        id: `run-${run.id}`, niveau: 'Verstoring', asset: 'Ingest',
        titel: `Ingesttaak ${run.taak} hangt`,
        tekst: `Staat al ${ouderdomTekst(nuMs - new Date(run.gestart_op).getTime())} op bezig.`,
        wanneer: run.gestart_op,
      });
    }
  }

  const orde: Record<string, number> = { Kritiek: 0, Verstoring: 1, Vraag: 2 };
  signalen.sort((a, b) => orde[a.niveau] - orde[b.niveau] || String(b.wanneer).localeCompare(String(a.wanneer)));

  const geslaagd = runs.filter((r) => r.status === 'klaar').length;

  return {
    regels,
    signalen,
    nieuwste: nieuwsteAlle,
    ouderdomMs: nieuwsteMs === null ? null : nuMs - nieuwsteMs,
    levert: regels.filter((r) => r.levert).length,
    totaal: regels.length,
    runs: { geslaagd, totaal: runs.length, records: runs.reduce((a, r) => a + (r.records || 0), 0) },
  };
}

/** Een tijdstip leesbaar, in de tijdzone van de locatie. */
export function tijdTekst(iso: string | null | undefined, zone: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', {
    timeZone: eisZone(zone),
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
